import { useEffect, useRef, useState } from 'react'
import { Pencil, X } from 'lucide-react'
import { getSocket } from '../services/socket'
import { AnnotateToolbar, TOOLBAR_COLORS } from '../components/voice/AnnotateToolbar'
import { renderStroke, renderLaser } from '../components/voice/drawing/strokeRenderers'
import type { Stroke, StrokeKind } from '../components/voice/drawing/types'

export default function ScreenOverlayRoute() {
  const params = new URLSearchParams(window.location.search)
  const channelId = params.get('channelId') || ''
  const sessionId = params.get('sessionId') || ''
  const canvasRef = useRef<HTMLCanvasElement>(null)

  const [annotateMode, setAnnotateMode] = useState(false)
  // Minimized = tools hidden (pencil reopen) but the overlay window stays alive
  // and annotations keep rendering. The old close button destroyed the window,
  // so you couldn't reopen it and the on-screen drawings vanished.
  const [minimized, setMinimized] = useState(false)
  const [activeTool, setActiveTool] = useState<StrokeKind | 'laser'>('pen')
  // OSPROD-205: no manual colour pick — stroke colour is auto-assigned per user
  // by the canvas (colourForUser); the picker only misled. Keep a stable brush
  // preview colour.
  const activeColor = TOOLBAR_COLORS[4]
  const [brushSize, setBrushSize] = useState<number>(4)
  const [strokes, setStrokes] = useState<Stroke[]>([])
  const [lasers, setLasers] = useState<Array<{ id: string; x: number; y: number; color: string; t: number }>>([])
  const [ownStrokeCount, setOwnStrokeCount] = useState(0)
  const [ownRedoCount, setOwnRedoCount] = useState(0)
  const [currentStroke, setCurrentStroke] = useState<Array<{ x: number; y: number }> | null>(null)
  const [shapeAnchor, setShapeAnchor] = useState<{ x: number; y: number } | null>(null)
  const lastLaserTsRef = useRef(0)

  const myId = localStorage.getItem('userId') || 'me'
  const myName = localStorage.getItem('userName') || 'You'

  // True while the pointer is over the toolbar — then the overlay must capture
  // input even in click-through mode so the buttons are actually clickable.
  const hoverRef = useRef(false)

  const overlayApi = () => window.electronAPI?.screenOverlay

  // Flip the Electron window between click-through (mouse passes to the real
  // desktop) and interactive. No-op / never throws when not under Electron.
  const setClickThrough = (ignore: boolean) => {
    overlayApi()?.setIgnoreMouse?.(ignore)?.catch(() => {/* ignore */})
  }

  // Make the real desktop show through. The SPA paints an opaque theme
  // background (white in light theme) on html/body, which otherwise covers the
  // transparent Electron window with a solid sheet (the "white canvas" bug).
  // embed-mode clears body + #root child; we additionally force html/body
  // transparent so nothing paints over the shared screen. Restored on unmount.
  useEffect(() => {
    const html = document.documentElement
    const body = document.body
    const prevHtml = html.style.background
    const prevBody = body.style.background
    body.classList.add('embed-mode')
    html.style.background = 'transparent'
    body.style.background = 'transparent'
    return () => {
      body.classList.remove('embed-mode')
      html.style.background = prevHtml
      body.style.background = prevBody
    }
  }, [])

  // Socket subscribe + initial replay request
  useEffect(() => {
    if (!channelId || !sessionId) return
    const socket = getSocket()
    socket.emit('voice:join-overlay', { channelId, sessionId })

    const onStroke = (s: Stroke & { sessionId: string }) => {
      if (s.sessionId !== sessionId) return
      setStrokes((prev) => [...prev, s])
    }
    const onReplay = (d: { sessionId: string; strokes: Stroke[] }) => {
      if (d.sessionId !== sessionId) return
      setStrokes(d.strokes || [])
    }
    const onClear = (d: { sessionId: string }) => {
      if (d.sessionId !== sessionId) return
      setStrokes([])
    }
    const onUndo = (d: { sessionId: string; strokeId: string }) => {
      if (d.sessionId !== sessionId) return
      setStrokes((prev) => prev.filter((x) => x.strokeId !== d.strokeId))
    }
    const onEvict = (d: { sessionId: string; strokeIds: string[] }) => {
      if (d.sessionId !== sessionId) return
      setStrokes((prev) => prev.filter((x) => !d.strokeIds.includes(x.strokeId)))
    }
    const onLaser = (d: { sessionId: string; x: number; y: number; color: string }) => {
      if (d.sessionId !== sessionId) return
      setLasers((prev) => [...prev, { id: crypto.randomUUID(), x: d.x, y: d.y, color: d.color, t: Date.now() }])
    }

    socket.on('draw:stroke', onStroke)
    socket.on('draw:replay', onReplay)
    socket.on('draw:clear', onClear)
    socket.on('draw:undo', onUndo)
    socket.on('draw:evict', onEvict)
    socket.on('draw:laser', onLaser)
    return () => {
      socket.off('draw:stroke', onStroke)
      socket.off('draw:replay', onReplay)
      socket.off('draw:clear', onClear)
      socket.off('draw:undo', onUndo)
      socket.off('draw:evict', onEvict)
      socket.off('draw:laser', onLaser)
    }
  }, [channelId, sessionId])

  // Paint loop — runs on every state change
  useEffect(() => {
    const c = canvasRef.current
    if (!c) return
    const dpr = window.devicePixelRatio || 1
    if (c.width !== window.innerWidth * dpr) c.width = window.innerWidth * dpr
    if (c.height !== window.innerHeight * dpr) c.height = window.innerHeight * dpr
    const ctx = c.getContext('2d')
    if (!ctx) return
    ctx.save()
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    ctx.clearRect(0, 0, window.innerWidth, window.innerHeight)
    for (const s of strokes) {
      renderStroke({ ctx, stroke: s, canvasWidth: window.innerWidth, canvasHeight: window.innerHeight })
    }
    for (const l of lasers) {
      renderLaser(ctx, l.x * window.innerWidth, l.y * window.innerHeight, l.color, Date.now() - l.t)
    }
    ctx.restore()
  }, [strokes, lasers])

  // Laser decay loop
  useEffect(() => {
    if (lasers.length === 0) return
    const id = requestAnimationFrame(() => {
      setLasers((prev) => prev.filter((l) => Date.now() - l.t < 1500))
    })
    return () => cancelAnimationFrame(id)
  }, [lasers])

  // Click-through base state: default = pass the mouse to the real desktop so
  // the overlay NEVER blocks the user (the earlier "white sheet you couldn't
  // close" trap). Annotating = capture everywhere so you can draw on the whole
  // screen. Hovering the toolbar keeps it interactive (hoverRef). The window is
  // created with setIgnoreMouseEvents(true,{forward:true}) so move/hover events
  // still reach us while click-through.
  useEffect(() => {
    setClickThrough(!(annotateMode || hoverRef.current))
  }, [annotateMode])

  // Esc exits annotate mode (non-destructive — never tears down the overlay).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && annotateMode) setAnnotateMode(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [annotateMode])

  const stagePoint = (e: React.PointerEvent): { x: number; y: number } => {
    return { x: e.clientX / window.innerWidth, y: e.clientY / window.innerHeight }
  }

  const onDown = (e: React.PointerEvent) => {
    if (!annotateMode) return
    const p = stagePoint(e)
    ;(e.target as Element).setPointerCapture?.(e.pointerId)
    if (activeTool === 'pen' || activeTool === 'highlighter' || activeTool === 'eraser') {
      setCurrentStroke([p])
    } else if (
      activeTool === 'line' || activeTool === 'arrow' ||
      activeTool === 'rect' || activeTool === 'oval' || activeTool === 'stamp'
    ) {
      setShapeAnchor(p)
    } else if (activeTool === 'text') {
      // NOTE: native prompt - runs in the standalone screen-overlay window, which
      // does not host the app-root <DialogHost/>. Intentionally not migrated.
      const value = window.prompt('Text:')
      if (value && value.trim()) {
        const stroke: Stroke = {
          strokeId: crypto.randomUUID(),
          channelId, sessionId, userId: myId, username: myName,
          color: activeColor, brushWidth: brushSize, timestamp: 0,
          kind: 'text', text: { value: value.trim(), fontSize: 18, x: p.x, y: p.y },
        }
        setStrokes((prev) => [...prev, stroke])
        setOwnStrokeCount((c) => c + 1)
        setOwnRedoCount(0)
        getSocket().emit('draw:stroke', stroke)
      }
    } else if (activeTool === 'laser') {
      getSocket().emit('draw:laser', {
        channelId, sessionId, userId: myId, color: activeColor, x: p.x, y: p.y,
      })
    }
  }

  const onMove = (e: React.PointerEvent) => {
    if (!annotateMode) return
    const p = stagePoint(e)
    if (currentStroke && (activeTool === 'pen' || activeTool === 'highlighter' || activeTool === 'eraser')) {
      setCurrentStroke((cur) => (cur ? [...cur, p] : null))
    } else if (activeTool === 'laser') {
      const now = Date.now()
      if (now - lastLaserTsRef.current > 33) {
        lastLaserTsRef.current = now
        getSocket().emit('draw:laser', {
          channelId, sessionId, userId: myId, color: activeColor, x: p.x, y: p.y,
        })
      }
    }
    // MVP: no live shape preview (line/arrow/rect/oval reveal on pointerup). Phase 2 polish.
  }

  const onUp = (e: React.PointerEvent) => {
    if (!annotateMode) return
    const p = stagePoint(e)
    const base = {
      strokeId: crypto.randomUUID(),
      channelId, sessionId, userId: myId, username: myName,
      color: activeColor, brushWidth: brushSize, timestamp: 0,
    }
    if (currentStroke && (activeTool === 'pen' || activeTool === 'highlighter' || activeTool === 'eraser')) {
      const stroke: Stroke = { ...base, kind: activeTool, points: currentStroke }
      setStrokes((prev) => [...prev, stroke])
      setOwnStrokeCount((c) => c + 1)
      setOwnRedoCount(0)
      getSocket().emit('draw:stroke', stroke)
      setCurrentStroke(null)
    } else if (shapeAnchor && (
      activeTool === 'line' || activeTool === 'arrow' ||
      activeTool === 'rect' || activeTool === 'oval'
    )) {
      const stroke: Stroke = {
        ...base, kind: activeTool,
        geometry: { x1: shapeAnchor.x, y1: shapeAnchor.y, x2: p.x, y2: p.y },
      }
      setStrokes((prev) => [...prev, stroke])
      setOwnStrokeCount((c) => c + 1)
      setOwnRedoCount(0)
      getSocket().emit('draw:stroke', stroke)
      setShapeAnchor(null)
    } else if (shapeAnchor && activeTool === 'stamp') {
      const stroke: Stroke = {
        ...base, kind: 'stamp', stampType: 'check',
        geometry: { x1: shapeAnchor.x, y1: shapeAnchor.y, x2: shapeAnchor.x, y2: shapeAnchor.y },
      }
      setStrokes((prev) => [...prev, stroke])
      setOwnStrokeCount((c) => c + 1)
      setOwnRedoCount(0)
      getSocket().emit('draw:stroke', stroke)
      setShapeAnchor(null)
    }
  }

  const onUndo = () => {
    getSocket().emit('draw:undo', { channelId, sessionId, userId: myId })
    setOwnStrokeCount((c) => Math.max(0, c - 1))
    setOwnRedoCount((c) => c + 1)
  }
  const onRedo = () => {
    getSocket().emit('draw:redo', { channelId, sessionId, userId: myId })
    setOwnRedoCount((c) => Math.max(0, c - 1))
    setOwnStrokeCount((c) => c + 1)
  }
  const onClear = () => getSocket().emit('draw:clear', { channelId, sessionId })
  const onSave = () => {
    const c = canvasRef.current
    if (!c) return
    const a = document.createElement('a')
    a.href = c.toDataURL('image/png')
    a.download = `annotation-${new Date().toISOString().replace(/[:.]/g, '-')}.png`
    a.click()
  }

  return (
    <div className="fixed inset-0" style={{ background: 'transparent', pointerEvents: 'none' }}>
      {/* Green border — frames the shared screen. Hidden while minimized to
          declutter; annotations keep rendering on the canvas below. */}
      {!minimized && (
        <div
          className="absolute inset-0"
          style={{ boxShadow: 'inset 0 0 0 4px #22c55e', pointerEvents: 'none' }}
        />
      )}
      {/* Canvas — pointer-events flip with annotate mode */}
      <canvas
        ref={canvasRef}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        style={{
          position: 'absolute', inset: 0, width: '100%', height: '100%',
          pointerEvents: (annotateMode && !minimized) ? 'auto' : 'none',
          cursor: (annotateMode && !minimized) ? 'crosshair' : 'default',
        }}
      />
      {/* Toolbar — collapsed pill OR expanded toolbar. Hovering captures input
          so the buttons work even while the rest of the overlay is
          click-through (see hoverRef / setClickThrough). */}
      <div
        className="absolute top-3 left-1/2 -translate-x-1/2 flex items-center gap-2"
        style={{ pointerEvents: 'auto' }}
        onMouseEnter={() => { hoverRef.current = true; setClickThrough(false) }}
        onMouseLeave={() => { hoverRef.current = false; setClickThrough(!annotateMode) }}
      >
        {minimized ? (
          /* Dormant: tools hidden, annotations stay on screen. Brings tools back. */
          <button
            onClick={() => setMinimized(false)}
            title="Show annotation tools"
            aria-label="Show annotation tools"
            className="bg-emerald-600/80 hover:bg-emerald-500 text-white rounded-full w-9 h-9 flex items-center justify-center shadow-2xl border border-emerald-400/40"
          >
            <Pencil size={16} />
          </button>
        ) : (
          <>
            {annotateMode ? (
              <AnnotateToolbar
                activeTool={activeTool}
                activeColor={activeColor}
                brushSize={brushSize}
                canUndo={ownStrokeCount > 0}
                canRedo={ownRedoCount > 0}
                onSelectTool={setActiveTool}
                onBrushSize={setBrushSize}
                onUndo={onUndo}
                onRedo={onRedo}
                onSave={onSave}
                onClear={onClear}
                onExit={() => setAnnotateMode(false)}
              />
            ) : (
              <button
                onClick={() => setAnnotateMode(true)}
                className="bg-emerald-600 hover:bg-emerald-500 text-white rounded-full px-4 py-2 font-medium shadow-2xl border border-emerald-400/40"
              >
                ✎ Annotate
              </button>
            )}
            {/* Hide the tools — drawings STAY on screen and the overlay stays
                alive (it only closes on stop-share), so you can reopen the tools
                with the pencil. Replaces the old destroy-on-close that left you
                unable to reopen and wiped the on-screen drawings. */}
            <button
              onClick={() => { setAnnotateMode(false); setMinimized(true) }}
              title="Hide annotation tools"
              aria-label="Hide annotation tools"
              className="w-9 h-9 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center shadow-2xl border border-white/15"
            >
              <X size={18} />
            </button>
          </>
        )}
      </div>
    </div>
  )
}
